import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { Layout } from '../components/Layout';
import { reportAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  BarChart3,
  Download,
  Loader2,
  Clock,
  Users,
  Calendar,
  TrendingUp,
} from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';

export default function Reports() {
  const { user, canManage } = useAuth();
  const { selectedLocationId, selectedLocation, isAllLocations } = useLocation();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [hoursReport, setHoursReport] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchReports();
  }, [user, dateRange, selectedLocationId]);

  const fetchReports = async () => {
    const locId = isAllLocations ? user?.restaurant_id : selectedLocationId;
    if (!locId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [hoursRes, summaryRes] = await Promise.all([
        reportAPI.getHours(locId, dateRange.start, dateRange.end),
        reportAPI.getWeeklySummary(locId, dateRange.start),
      ]);

      setHoursReport(hoursRes.data);
      setWeeklySummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    const locId = isAllLocations ? user?.restaurant_id : selectedLocationId;
    const locSlug = isAllLocations
      ? 'all-locations'
      : (selectedLocation?.location_name || 'report').toLowerCase().replace(/\s+/g, '-');
    try {
      const response = await reportAPI.exportCSV(locId, dateRange.start, dateRange.end);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `labor-report_${locSlug}_${dateRange.start}_to_${dateRange.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    }
  };

  const buildReportContainer = () => {
    const MAX_ROWS   = 25;
    const rows       = hoursReport.slice(0, MAX_ROWS);
    const truncated  = hoursReport.length > MAX_ROWS;
    const totalHours = hoursReport.reduce((s, r) => s + (r.total_hours || 0), 0);
    const avgHours   = hoursReport.length ? (totalHours / hoursReport.length).toFixed(1) : '0.0';
    const locName    = isAllLocations ? 'All Locations' : (selectedLocation?.location_name || '');
    const today      = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const TH  = 'padding:10px 14px;text-align:left;font-weight:600;font-size:12px;';
    const TH_R= 'padding:10px 14px;text-align:right;font-weight:600;font-size:12px;';
    const TD  = 'padding:9px 14px;text-align:left;font-size:12px;color:#334155;border-bottom:1px solid #e2e8f0;';
    const TD_R= 'padding:9px 14px;text-align:right;font-size:12px;color:#334155;border-bottom:1px solid #e2e8f0;';

    const rowsHtml = rows.map((r, i) => {
      const bg  = i % 2 === 0 ? '#ffffff' : '#f8fafc';
      const avg = r.shift_count ? (r.total_hours / r.shift_count).toFixed(1) : '—';
      return `<tr style="background:${bg};">
        <td style="${TD}">${r.user_name || '—'}</td>
        <td style="${TD_R}">${r.total_hours.toFixed(1)}</td>
        <td style="${TD_R}">${r.shift_count}</td>
        <td style="${TD_R}">${avg}</td>
      </tr>`;
    }).join('');

    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1024px;background:#ffffff;padding:32px;font-family:ui-sans-serif,system-ui,sans-serif;box-sizing:border-box;';
    container.innerHTML = `
      <div style="border-bottom:2px solid #1e293b;padding-bottom:14px;margin-bottom:22px;display:flex;justify-content:space-between;align-items:flex-end;">
        <div>
          <h1 style="font-size:22px;font-weight:700;color:#1e293b;margin:0 0 4px;">Workforce Report</h1>
          ${locName ? `<p style="font-size:13px;color:#64748b;margin:0 0 2px;">${locName}</p>` : ''}
          <p style="font-size:13px;color:#64748b;margin:0;">Period: ${dateRange.start} &ndash; ${dateRange.end}</p>
        </div>
        <p style="font-size:12px;color:#94a3b8;margin:0;">Generated ${today}</p>
      </div>

      <div style="display:flex;gap:16px;margin-bottom:24px;">
        <div style="flex:1;background:#f0f9ff;border:1px solid #bae6fd;border-radius:8px;padding:14px 18px;">
          <p style="font-size:11px;color:#0369a1;font-weight:600;text-transform:uppercase;margin:0 0 4px;">Total Hours</p>
          <p style="font-size:24px;font-weight:700;color:#1e293b;margin:0;">${totalHours.toFixed(1)}</p>
        </div>
        <div style="flex:1;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:14px 18px;">
          <p style="font-size:11px;color:#166534;font-weight:600;text-transform:uppercase;margin:0 0 4px;">Employees</p>
          <p style="font-size:24px;font-weight:700;color:#1e293b;margin:0;">${hoursReport.length}</p>
        </div>
        <div style="flex:1;background:#fefce8;border:1px solid #fde68a;border-radius:8px;padding:14px 18px;">
          <p style="font-size:11px;color:#92400e;font-weight:600;text-transform:uppercase;margin:0 0 4px;">Avg Hrs / Employee</p>
          <p style="font-size:24px;font-weight:700;color:#1e293b;margin:0;">${avgHours}</p>
        </div>
        ${weeklySummary ? `<div style="flex:1;background:#fdf4ff;border:1px solid #e9d5ff;border-radius:8px;padding:14px 18px;">
          <p style="font-size:11px;color:#6b21a8;font-weight:600;text-transform:uppercase;margin:0 0 4px;">Total Shifts</p>
          <p style="font-size:24px;font-weight:700;color:#1e293b;margin:0;">${weeklySummary.total_shifts}</p>
        </div>` : ''}
      </div>

      <table style="width:100%;border-collapse:collapse;">
        <thead>
          <tr style="background:#1e293b;color:#ffffff;">
            <th style="${TH}">Employee</th>
            <th style="${TH_R}">Total Hours</th>
            <th style="${TH_R}">Shifts</th>
            <th style="${TH_R}">Avg Hrs / Shift</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot>
          <tr style="background:#f1f5f9;">
            <td style="padding:9px 14px;font-weight:700;font-size:12px;color:#1e293b;">Total</td>
            <td style="padding:9px 14px;text-align:right;font-weight:700;font-size:12px;color:#1e293b;">${totalHours.toFixed(1)}</td>
            <td colspan="2"></td>
          </tr>
        </tfoot>
      </table>
      ${truncated ? `<p style="margin-top:14px;font-size:11px;color:#94a3b8;text-align:center;">Showing first ${MAX_ROWS} of ${hoursReport.length} employees — export CSV for complete data.</p>` : ''}
    `;
    return container;
  };

  const handleExportPNG = async () => {
    if (!hoursReport.length) { toast.error('No data to export'); return; }
    setExporting('png');
    try {
      const { default: html2canvas } = await import('html2canvas');
      const container = buildReportContainer();
      document.body.appendChild(container);
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, logging: false, width: 1024, windowWidth: 1024 });
      document.body.removeChild(container);
      const link     = document.createElement('a');
      link.download  = `report-${dateRange.start}.png`;
      link.href      = canvas.toDataURL('image/png');
      link.click();
      toast.success('PNG downloaded');
    } catch (err) {
      console.error(err); toast.error('PNG export failed');
    } finally { setExporting(''); }
  };

  const handleExportPDF = async () => {
    if (!hoursReport.length) { toast.error('No data to export'); return; }
    setExporting('pdf');
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
      const container = buildReportContainer();
      document.body.appendChild(container);
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, logging: false, width: 1024, windowWidth: 1024 });
      document.body.removeChild(container);

      const imgData  = canvas.toDataURL('image/png');
      const pdf      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW    = pdf.internal.pageSize.getWidth();
      const pageH    = pdf.internal.pageSize.getHeight();
      const margin   = 10;
      const usableW  = pageW - margin * 2;
      const imgH     = (canvas.height / canvas.width) * usableW;
      const finalH   = Math.min(imgH, pageH - margin * 2);
      pdf.addImage(imgData, 'PNG', margin, margin, usableW, finalH);
      pdf.save(`report-${dateRange.start}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err); toast.error('PDF export failed');
    } finally { setExporting(''); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Reports
              {canManage && <HelpTooltip text="Reports provide detailed analytics on staff hours, shift activity, and workforce trends. Data can be exported for operational or payroll analysis." learnMoreUrl="/help/reports" />}
            </h1>
            <p className="text-slate-500 mt-1">
              Hours tracking and team analytics
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExportCSV} disabled={!!exporting || loading} data-testid="export-csv-btn">
              <Download className="mr-2 h-4 w-4" />
              CSV
            </Button>
            <Button variant="outline" onClick={handleExportPNG} disabled={!!exporting || loading || !hoursReport.length} data-testid="export-png-btn">
              {exporting === 'png' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              PNG
            </Button>
            <Button onClick={handleExportPDF} disabled={!!exporting || loading || !hoursReport.length} data-testid="export-pdf-btn">
              {exporting === 'pdf' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              PDF
            </Button>
          </div>
        </div>

        {/* Date Range Filter */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-end gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  data-testid="report-start-date"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  data-testid="report-end-date"
                />
              </div>
              <Button variant="outline" onClick={fetchReports} data-testid="apply-filter-btn">
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Summary */}
        {weeklySummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{weeklySummary.total_hours}</p>
                    <p className="text-sm text-slate-500">Total Hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Calendar className="text-green-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{weeklySummary.total_shifts}</p>
                    <p className="text-sm text-slate-500">Total Shifts</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{weeklySummary.active_staff}</p>
                    <p className="text-sm text-slate-500">Active Staff</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{weeklySummary.tasks_completed}</p>
                    <p className="text-sm text-slate-500">Tasks Done</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Hours by Staff */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 size={20} className="text-slate-600" />
              Hours by Staff
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hoursReport.length === 0 ? (
              <div className="text-center py-10" data-testid="reports-hours-empty-state">
                <Clock className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-2 text-slate-500 font-medium">No shift data for this period</p>
                <p className="text-sm text-slate-400 mt-1 mb-4">Add shifts to the schedule to generate report data</p>
                <Button variant="outline" size="sm" onClick={() => { window.location.href = '/schedule'; }} data-testid="reports-empty-cta">
                  + Go to Schedule
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {hoursReport.map((item, index) => {
                  const maxHours = Math.max(...hoursReport.map(r => r.total_hours));
                  const percentage = (item.total_hours / maxHours) * 100;

                  return (
                    <div key={index} className="space-y-2" data-testid={`hours-row-${index}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">{item.user_name}</span>
                        <span className="text-sm text-slate-600">
                          {item.total_hours.toFixed(1)} hours ({item.shift_count} shifts)
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-500 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Detailed Hours</CardTitle>
          </CardHeader>
          <CardContent>
            {hoursReport.length === 0 ? (
              <div className="text-center py-8" data-testid="reports-table-empty-state">
                <p className="text-slate-500 font-medium">No data available for this period</p>
                <p className="text-sm text-slate-400 mt-1 mb-3">Try a different date range or add shifts to the schedule</p>
                <Button variant="outline" size="sm" onClick={() => { window.location.href = '/schedule'; }} data-testid="reports-table-empty-cta">
                  + Go to Schedule
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Staff Member</th>
                      <th>Total Hours</th>
                      <th>Shifts</th>
                      <th>Avg Hours/Shift</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoursReport.map((item, index) => (
                      <tr key={index}>
                        <td className="font-medium text-slate-900">{item.user_name}</td>
                        <td>{item.total_hours.toFixed(1)}</td>
                        <td>{item.shift_count}</td>
                        <td>{(item.total_hours / item.shift_count).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
